import { getSharedApiService } from './apiService';
import { apiRoutes } from '@/config/routes';
import type { CreateUserRequest, CreateUserResponse } from '@/types/user';
import { UserRole } from '@/types/auth';

/**
 * User list response item from API
 * Matches the response structure from /identity/api/admin/users
 */
export interface UserListItem {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  status: 'active' | 'inactive' | 'pending';
  created_by: string | null;
  created_at: string;
  role: UserRole;
}

/**
 * Paginated user list response from API
 */
export interface UserListResponse {
  users: UserListItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

/**
 * Query parameters for filtering and paginating users
 */
export interface GetUsersParams {
  /** Page number (default: 1) */
  page?: number;
  /** Number of items per page (default: 10) */
  page_size?: number;
  /** Filter by role */
  role?: string;
  /** Filter by status: 'accepted' or 'pending' */
  status?: 'active' | 'inactive' | 'pending'|'accepted';
  /** Search query string */
  search?: string;
  /** Start date for date range filter (format: YYYY-MM-DD) */
  start_date?: string;
  /** End date for date range filter (format: YYYY-MM-DD) */
  end_date?: string;
}

/**
 * Get list of users with pagination and filtering
 * @param params - Query parameters for pagination, filtering, and search
 * @returns Promise<UserListResponse>
 */
export const getUsers = async (params?: GetUsersParams): Promise<UserListResponse> => {
  const api = getSharedApiService();

  try {
    // Build query parameters
    const queryParams = new URLSearchParams();
    
    if (params?.page !== undefined) {
      queryParams.append('page', params.page.toString());
    } else {
      queryParams.append('page', '1');
    }
    
    if (params?.page_size !== undefined) {
      queryParams.append('page_size', params.page_size.toString());
    } else {
      queryParams.append('page_size', '10');
    }
    
    if (params?.role) {
      queryParams.append('role', params.role);
    }
    
    if (params?.status) {
      queryParams.append('status', params.status);
    }
    
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    
    if (params?.start_date) {
      queryParams.append('start_date', params.start_date);
    }
    
    if (params?.end_date) {
      queryParams.append('end_date', params.end_date);
    }

    // Build URL with query parameters
    const url = `${apiRoutes.admin.listUsers}?${queryParams.toString()}`;
    const response = await api.get<UserListResponse>(url);

    return response.data;
  } catch (error) {
    console.error('Get users error:', error);
    throw error;
  }
};

/**
 * Update password request payload
 */
export interface UpdatePasswordRequest {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

/**
 * Update password response
 */
export interface UpdatePasswordResponse {
  message?: string;
  success?: boolean;
}

/**
 * Delete user request payload
 */
export interface DeleteUserRequest {
  email: string;
}

/**
 * Delete user response
 */
export interface DeleteUserResponse {
  message?: string;
  success?: boolean;
}

/**
 * Invite action request payload
 */
export interface InviteActionRequest {
  email: string;
  action: 'resend' | 'revoke';
  reason?: string;
}

/**
 * Invite action response
 */
export interface InviteActionResponse {
  message?: string;
  success?: boolean;
}

/**
 * Update current user's password
 * @param passwordData - Password update data
 * @returns Promise<UpdatePasswordResponse>
 */
export const updatePassword = async (passwordData: UpdatePasswordRequest): Promise<UpdatePasswordResponse> => {
  const api = getSharedApiService();

  try {
    const response = await api.post<{ data?: UpdatePasswordResponse } | UpdatePasswordResponse>(
      apiRoutes.auth.resetCurrentPassword,
      passwordData
    );

    // Handle both wrapped and direct response structures
    const responseData = (response.data && 'data' in response.data && response.data.data)
      ? response.data.data
      : (response.data as UpdatePasswordResponse);

    return responseData;
  } catch (error) {
    console.error('Update password error:', error);
    throw error;
  }
};

/**
 * Delete a user
 * @param email - User email to delete
 * @returns Promise<DeleteUserResponse>
 */
export const deleteUser = async (email: string): Promise<DeleteUserResponse> => {
  const api = getSharedApiService();

  try {
    const response = await api.delete<{ data?: DeleteUserResponse } | DeleteUserResponse>(
      apiRoutes.admin.deleteUser,
      {
        data: { email } as DeleteUserRequest,
      }
    );

    // Handle both wrapped and direct response structures
    const responseData = (response.data && 'data' in response.data && response.data.data)
      ? response.data.data
      : (response.data as DeleteUserResponse);

    return responseData;
  } catch (error) {
    console.error('Delete user error:', error);
    throw error;
  }
};

/**
 * Perform invite action (resend or revoke)
 * @param actionData - Invite action data
 * @returns Promise<InviteActionResponse>
 */
export const inviteAction = async (actionData: InviteActionRequest): Promise<InviteActionResponse> => {
  const api = getSharedApiService();

  try {
    const queryParams = new URLSearchParams({
      action: actionData.action,
    });

    const requestBody: Record<string, string> = {
      email: actionData.email,
    };

    if (actionData.reason) {
      requestBody.reason = actionData.reason;
    }

    const response = await api.post<{ data?: InviteActionResponse } | InviteActionResponse>(
      `${apiRoutes.admin.inviteAction}?${queryParams.toString()}`,
      requestBody
    );

    // Handle both wrapped and direct response structures
    const responseData = (response.data && 'data' in response.data && response.data.data)
      ? response.data.data
      : (response.data as InviteActionResponse);

    return responseData;
  } catch (error) {
    console.error('Invite action error:', error);
    throw error;
  }
};

/**
 * Update user request payload
 */
export interface UpdateUserRequest {
  email: string;
  first_name: string;
  last_name: string;
  role?: string; // Optional - should not be included if logged-in user is USER
}

/**
 * Update user response
 */
export interface UpdateUserResponse {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  status?: string;
  dateAdded?: string;
}

/**
 * Update an existing user
 * @param userId - User ID to update
 * @param userData - User data to update
 * @returns Promise<UpdateUserResponse>
 */
export const updateUser = async (userId: string | number, userData: UpdateUserRequest): Promise<UpdateUserResponse> => {
  const api = getSharedApiService();

  try {
    const response = await api.put<{ data?: UpdateUserResponse } | UpdateUserResponse>(
      apiRoutes.admin.updateUser,
      { ...userData, id: userId }
    );

    // Handle both wrapped and direct response structures
    const responseData = (response.data && 'data' in response.data && response.data.data)
      ? response.data.data
      : (response.data as UpdateUserResponse);

    return {
      id: responseData.id || String(userId),
      username: responseData.username || `${userData.first_name} ${userData.last_name}`,
      email: responseData.email,
      first_name: responseData.first_name,
      last_name: responseData.last_name,
      role: responseData.role,
      status: responseData.status || 'active',
      dateAdded: responseData.dateAdded || new Date().toISOString().split('T')[0],
    };
  } catch (error) {
    console.error('Update user error:', error);
    throw error;
  }
};

/**
 * Create a new user
 * @param userData - User data to create
 * @returns Promise<CreateUserResponse>
 */
export const createUser = async (userData:CreateUserRequest): Promise<CreateUserResponse> => {
  const api = getSharedApiService();
  const username = userData.first_name+' '+userData.last_name;


  try {
    const response = await api.post<{ data?: CreateUserResponse } | CreateUserResponse>(
      apiRoutes.admin.createUser,
      {...userData, username: username}
    );

    // Handle both wrapped and direct response structures
    const responseData = (response.data && 'data' in response.data && response.data.data)
      ? response.data.data
      : (response.data as CreateUserResponse);

    return {
      id: responseData.id,
      username: responseData.username,
      email: responseData.email,
      first_name: responseData.first_name,
      last_name: responseData.last_name,
      role: responseData.role,
      status: responseData.status || 'pending',
      dateAdded: responseData.dateAdded || new Date().toISOString().split('T')[0],
    };
  } catch (error) {
    console.error('Create user error:', error);
    throw error;
  }
};

/**
 * Toggle user status request payload
 */
export interface ToggleUserStatusRequest {
  email: string;
  is_active: boolean;
}

/**
 * Toggle user status response
 */
export interface ToggleUserStatusResponse {
  message?: string;
  success?: boolean;
}

/**
 * Toggle user status (activate/deactivate)
 * @param statusData - Status toggle data
 * @returns Promise<ToggleUserStatusResponse>
 */
export const toggleUserStatus = async (statusData: ToggleUserStatusRequest): Promise<ToggleUserStatusResponse> => {
  const api = getSharedApiService();

  try {
    const response = await api.put<{ data?: ToggleUserStatusResponse } | ToggleUserStatusResponse>(
      apiRoutes.admin.toggleUserStatus,
      statusData
    );

    // Handle both wrapped and direct response structures
    const responseData = (response.data && 'data' in response.data && response.data.data)
      ? response.data.data
      : (response.data as ToggleUserStatusResponse);

    return responseData;
  } catch (error) {
    console.error('Toggle user status error:', error);
    throw error;
  }
};

